const debug = require('debug')('game:enemy');

/**
 * Example of a special ability
const poisonBite = {
  name: "Poison Bite",
  execute: (user, target) => {
  console.log(${user.name} uses ${poisonBite.name} on ${target.name}.);
    target.addStatusEffect('poisoned');
    // Additional effect logic
  }
};

// Usage example
const bandit = new HumanEnemy({
name: "Bandit Leader",
level: 5,
health: 100,
attackPower: 20,
defense: 10,
speed: 15,
agility: 12,
intelligence: 8,
weapon: { name: "Rusty Sword", damage: 5 },
isHostile: true,
description: "A dangerous bandit known for ambushing travelers.",
specialAbilities: [poisonBite]
});

const wolf = new AnimalEnemy({
name: "Alpha Wolf",
level: 3,
health: 80,
attackPower: 15,
defense: 5,
speed: 20,
agility: 18,
habitat: "forest",
isHostile: true,
description: "The leader of a wolf pack, fierce and territorial."
});
  */
class Enemy {
  constructor(options) {
    // Basic Information
    this.id = options.id || `enemy-${Math.random().toString(36).substr(2, 9)}`;
    this.type = options.type; // 'human', 'animal', etc.
    this.name = options.name; // Enemy's name
    this.description = options.description; // Optional: Brief description of the enemy
    this.level = options.level; // Enemy's level, used for scaling stats

    // Stats with Level Scaling and Randomness
    const baseHealth = this.getRandomStat(options.health);
    const baseAttackPower = this.getRandomStat(options.attackPower);
    const baseDefense = this.getRandomStat(options.defense);
    const baseSpeed = this.getRandomStat(options.speed);
    const baseAgility = this.getRandomStat(options.agility);
    const baseIntelligence = this.getRandomStat(options.intelligence);

    // Calculate the scaling factor with a 5% increase per level below maxLevel
    // This means an enemy at maxLevel will be at 100% of the base stats provided.
    // Levels below maxLevel scale down by 5% per level below max.
    const MAX_LEVEL = 10;
    const scalingFactor = 1 + (this.level - MAX_LEVEL) * 0.05;

    // Stats
    this.health = Math.round(baseHealth * scalingFactor);
    this.maxHealth = this.health; // Maximum health points, scaled
    this.attackPower = Math.round(baseAttackPower * scalingFactor); // Base attack damage, scaled
    this.defense = Math.round(baseDefense * scalingFactor); // Damage reduction from attacks, scaled
    this.speed = Math.round(baseSpeed * scalingFactor); // Determines attack order, scaled
    this.agility = Math.round(baseAgility * scalingFactor); // Affects ability to dodge or hit, scaled
    this.intelligence = Math.round(baseIntelligence * scalingFactor); // For special abilities or decisions, scaled

    this.statusEffects = options.statusEffects || []; // e.g., 'poisoned', 'stunned'

    // Combat Specifics
    this.isDead = false; // Flag indicating if the enemy is dead
    this.lootTable = options.lootTable; // Possible loot dropped by this enemy on defeat
    this.isHostile = options.isHostile; // Determines if automatically engages in combat
    this.specialAbilities = options.specialAbilities || []; // Special attacks or defenses with cooldowns
    // AI and Behavior (Optional)
    // define additional properties here for enemy behavior like:
    // - preferredTarget: string (e.g., "player", "weakest") - who the enemy attacks first
  }

  getRandomStat(base, variancePercent = 10) {
    const min = base - (base * variancePercent) / 100;
    const max = base + (base * variancePercent) / 100;
    return Math.floor(Math.random() * (max - min + 1) + min);
  }

  // Method to perform an attack
  attack(target) {
    const damage = Math.max(this.attackPower - target.defense, 0);
    target.receiveDamage(damage);
    return damage;
  }

  // Method to receive damage
  receiveDamage(amount) {
    this.health = Math.max(this.health - amount, 0);
    if (this.health === 0) {
      this.onDefeat();
    }
  }

  // Method to handle defeat of the enemy
  onDefeat() {
    debug(`${this.name} defeated.`);
    this.isDead = true;
    // Handle dropping loot, experience, etc.
  }

  // Method to add a status effect
  addStatusEffect(effect) {
    this.statusEffects.push(effect);
    // Handle application of status effect (could reduce speed, prevent actions, etc.)
  }

  // Method to remove a status effect
  removeStatusEffect(effect) {
    const index = this.statusEffects.indexOf(effect);
    if (index > -1) {
      this.statusEffects.splice(index, 1);
    }
    // Handle removal logic of the status effect
  }

  // Method to execute a special ability
  useSpecialAbility(abilityName, target) {
    const ability = this.specialAbilities.find(ability => ability.name === abilityName);
    if (ability) {
      ability.execute(this, target);
    }
  }
}

// Example of extending the Enemy class for specific types
class HumanEnemy extends Enemy {
  constructor(options) {
    super({ ...options, type: 'human' });
    this.weapon = options.weapon; // Specific to human enemies
  }

  // Overriding the onDefeat method for humans
  onDefeat() {
    super.onDefeat();
    debug(`${this.name} drops their weapon.`);
  }
}

class AnimalEnemy extends Enemy {
  constructor(options) {
    super({ ...options, type: 'animal' });
    this.habitat = options.habitat; // Specific to animal enemies
  }

  // Special behavior for animals
  flee() {
    if (this.health < this.maxHealth / 2) {
      debug(`${this.name} attempts to flee to safety.`);
      // Additional logic to handle fleeing
    }
  }
}

export { AnimalEnemy, Enemy, HumanEnemy };
